import { useEffect, useRef, useState } from "react"

import { Box, Stack, Typography } from "@mui/material"
import { isEmpty } from "lodash"
import { FormProvider } from "react-hook-form"
import { addNode, removeNode, useDispatch, useSelector } from "@ggs/store"
import type { FC } from ".."
import {
  Container,
  Pagination,
  SearchFacets,
  Text,
  TripCollection,
  TripItinerariesSearchForm,
  StickyTripBannerVar
} from ".."
import { mapTripTeaser, mapWishlistData } from "../../helpers/data-mappers"
import { useTripSearchState, useTripsSearchData } from "../../local/hooks"
import { getSortOption } from "../TripGeneralSearch/helpers"

type TripDestinationSearchProps = {
  title: string | null
  location: string
  path: string
  // experienceFilter is used to hardcode a specific experience filter (Trip Type or Trip Style)
  // when rendering the search on a Trip Idea page that matches one of those filters
  experienceFilter?: { type: string; id: string; name: string }
}

const TripDestinationSearch: FC<TripDestinationSearchProps> = ({
  title = null,
  location,
  path,
  experienceFilter = null,
}) => {
  const dispatch = useDispatch()
  const wishlist = useSelector((state) => state.views.wishlist)
  const userCountry = useSelector((state) => state.settings.country)
  const likedTrips = wishlist?.trip && wishlist?.trip.map((trip: any) => trip.nid)
  const { getTripsSolrDataByFilters } = useTripsSearchData()
  const listRef = useRef<HTMLDivElement>(null)
  const [tripSearchResults, setTripSearchResults] = useState<any>({
    count: 0,
    totalPages: 0,
    results: null,
    locations: [],
    experiences: [],
    prices: [],
    durations: [],
  })
  const hideFilters = ["country", ...(experienceFilter ? ["experiences"] : [])]
  const { form, handlers, state } = useTripSearchState({
    path,
    defaultState: {
      country: location,
    },
    ignoreFilters: hideFilters,
  })

  const fetchSolrTripsData = async (params: any) => {
    // Add experience filter type (Trip Types/Trip Styles) and ID if one is selected by default
    const selectedExperienceFilter = experienceFilter && {
      [experienceFilter.type]: experienceFilter.id,
    }
    const data = await getTripsSolrDataByFilters({
      ...params,
      ...selectedExperienceFilter,
    })
    const filteredData = mapWishlistData(likedTrips, data.results)
    setTripSearchResults({ ...data, results: filteredData })
  }

  const addToWishList = (selectedEntity: any) => {
    const filteredState = mapTripTeaser(tripSearchResults?.results, selectedEntity, true)
    setTripSearchResults({ ...tripSearchResults, results: filteredState })
    dispatch(
      addNode({ type: selectedEntity.contentType, entity: selectedEntity, idPropertyKey: "nid" })
    )
  }

  const removeFromWishList = (selectedEntity: any) => {
    const filteredState = mapTripTeaser(tripSearchResults?.results, selectedEntity, false)
    setTripSearchResults({ ...tripSearchResults, results: filteredState })
    dispatch(removeNode({ type: selectedEntity.contentType, findPredicate: selectedEntity }))
  }

  const params = state?.urlParams
  const searchParams = state?.searchState
  // Resolve Solr data.
  useEffect(() => {
    fetchSolrTripsData(searchParams)
  }, [searchParams, userCountry, experienceFilter])

  // resolve scroll top when page changes
  const handleScrollToTop = () => {
    if (listRef.current) {
      const listItemRect = listRef.current.getBoundingClientRect()
      const topOffset = window.scrollY + listItemRect.top - 150
      window.scrollTo({ top: topOffset, behavior: "smooth" })
    }
  }

  const stickyTripBannerProps = {

  }

  return (
    <FormProvider {...form}>
      <Container maxWidth={1560}>
        <Stack
          rowGap={{ xs: 15, md: 25 }}
          sx={(theme) => ({
            bgcolor: theme.palette["secondary.300"].main,
            p: { xs: 5, md: 10 },
            pt: { xs: 15, md: 30 },
            mt: { xs: 5, md: 10 },
            borderRadius: 1.5,
            position: "relative",
            ...(!title && {
              mt: 0,
              pt: { xs: 5, md: 10 },
            }),
          })}
        >
          {title && (
            <Typography
              variant="heading.2xl"
              component="h2"
              textAlign={{ md: "center" }}
              color={(theme) => theme.palette["secondary.800"].main}
              maxWidth={744}
              mx="auto"
            >
              {title}
            </Typography>
          )}
          <TripItinerariesSearchForm
            locations={tripSearchResults.locations}
            experiences={tripSearchResults.experiences}
            onApplyFilter={handlers.onApplyFilter}
            searchParams={searchParams}
            hideFilters={hideFilters}
          />
        </Stack>
      </Container>
      <Container
        maxWidth={1480}
        paddingBottom={tripSearchResults.totalPages > 1 ? 0 : 45}
      >
        <Box pt={5} pb={10}>
          <SearchFacets
            facets={state.usedFilters}
            sortOptions={[
              { label: "Duration, Short to Long", type: "its_duration-asc" },
              { label: "Duration, Long to Short", type: "its_duration-desc" },
              { label: "Price, Low to High", type: "price-asc" },
              { label: "Price, High to Low", type: "price-desc" },
            ]}
            count={tripSearchResults.count}
            removeAllFilters={handlers.removeAllFilters}
            removeIndividualFilter={handlers.removeIndividualFilter}
            sortChange={handlers.onSortChange}
            paramSort={getSortOption(params.sort as string)}
            onSpecialsToggle={handlers.onSpecialsToggle}
            isSpecialsChecked={params.specials}
          />
        </Box>
        <Box
          ref={listRef}
          borderBottom={(theme) => `1px solid ${theme.palette["neutral.100"].main}`}
        >
          {tripSearchResults.results !== null && isEmpty(tripSearchResults.results) ? (
            <Text title="No results found" text="Please review your search criteria." />
          ) : (
            <TripCollection items={tripSearchResults.results} showSpecialTag={params.specials} />
          )}
        </Box>
        {tripSearchResults.totalPages > 1 && (
          <Box pt={{ xs: 19, md: 20 }} pb={{ xs: 15, md: 11 }}>
            <Pagination
              page={parseInt(params.page as string) || 1}
              count={tripSearchResults.totalPages}
              onPageChange={(page: number) => handlers.onPageChange(page, handleScrollToTop)}
            />
          </Box>
        )}
      </Container>
      <StickyTripBannerVar
      />
    </FormProvider>
  )
}

export default TripDestinationSearch
